/**
 * Legal template: contents list and tables.
 *
 * Loaded only on pages using page-legal.php (inc/templates.php). The list, its
 * anchors and the copy all come from the server — this only lights the entry
 * you are reading and gives wide tables their own scroll frame.
 */

/** Distance from the top that counts as "you are here", clear of the header. */
const READING_LINE = 120;

const initContents = (): void => {
  const toc = document.querySelector<HTMLElement>('[data-legal-toc]');
  const body = document.querySelector<HTMLElement>('[data-legal-body]');

  if (!toc || !body) {
    return;
  }

  const inner = toc.querySelector<HTMLElement>('.legal-toc__inner');
  const marker = toc.querySelector<HTMLElement>('[data-toc-marker]');

  const sections = Array.from(toc.querySelectorAll<HTMLAnchorElement>('[data-toc-link]'))
    .map((link) => {
      const id = decodeURIComponent(link.getAttribute('href')?.slice(1) ?? '');

      return { link, heading: id ? body.querySelector<HTMLElement>(`#${CSS.escape(id)}`) : null };
    })
    .filter((entry): entry is { link: HTMLAnchorElement; heading: HTMLElement } => !!entry.heading);

  if (!sections.length) {
    return;
  }

  /**
   * Move the highlight onto an entry.
   *
   * Measured from the rendered boxes rather than a fixed row height: a long
   * section name wraps to two lines and the highlight has to cover it.
   */
  const place = (link: HTMLElement): void => {
    if (!inner || !marker) {
      return;
    }

    const box = link.getBoundingClientRect();

    marker.style.translate = `0 ${Math.round(box.top - inner.getBoundingClientRect().top)}px`;
    marker.style.height = `${Math.round(box.height)}px`;
  };

  let active = -1;

  const spy = (): void => {
    let current = 0;

    sections.forEach((section, index) => {
      if (section.heading.getBoundingClientRect().top <= READING_LINE) {
        current = index;
      }
    });

    // The last section is often too short to ever reach the line, so hitting the
    // bottom of the page hands it the highlight.
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      current = sections.length - 1;
    }

    if (current === active) {
      return;
    }

    sections[active]?.link.classList.remove('is-active');
    sections[current].link.classList.add('is-active');
    place(sections[current].link);
    active = current;
  };

  let queued = false;

  const request = (): void => {
    if (queued) {
      return;
    }

    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      spy();
    });
  };

  window.addEventListener('scroll', request, { passive: true });

  // A resize moves the entries without changing which one is lit, so the
  // highlight is re-placed directly rather than through the spy.
  window.addEventListener('resize', () => {
    request();

    if (sections[active]) {
      place(sections[active].link);
    }
  });

  spy();

  // Only now does the highlight have somewhere to be, so only now may it animate.
  requestAnimationFrame(() => inner?.classList.add('is-ready'));
};

/**
 * Tables.
 *
 * The editor writes a plain <table>; this wraps it so a wide one scrolls inside
 * its own frame instead of stretching the page.
 */
const initTables = (): void => {
  const body = document.querySelector<HTMLElement>('[data-legal-body]');

  if (!body) {
    return;
  }

  body.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    if (table.closest('.legal-table')) {
      return;
    }

    const frame = document.createElement('div');
    frame.className = 'legal-table';
    frame.setAttribute('role', 'region');
    frame.setAttribute('tabindex', '0');

    table.parentNode?.insertBefore(frame, table);
    frame.appendChild(table);
  });
};

const boot = (): void => {
  initContents();
  initTables();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// No imports here, so this marks the file as a module and keeps its
// top-level names out of the global scope other blocks share.
export {};
